import React from 'react';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import { KitCard } from 'components/common/cms-kit';
import { buttonColor } from 'services/styleUtils/buttonStyleUtils';
import { useTheme } from '@mui/material/styles';
import DisplayLink from 'components/common/subcomponents/DisplayLink';

const CardGrid = styled('div')(({ theme }) => ({
  width: '90%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '1.75em 2.00em',
  [theme.breakpoints.up('md')]: {
    margin: '20px 0 0 0',
    padding: '10px 0',
  },
  [theme.breakpoints.down('md')]: {
    margin: '10px 0 0 0',
    padding: '10px 0',
  },
}));
const CardImg = styled('img')(() => ({
  display: 'block',
  width: '85%',
  height: '140px',
  objectFit: 'contain',
  margin: '10px auto 0 auto',
  borderRadius: '6px',
  backgroundColor: 'transparent',
}));

const IconCardList = ({ module }) => {
  const { fields } = module;
  const theme = useTheme();

  const Container = styled.div`
    background-color: #f5f5f5;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    // margin: 25px 0;
    padding: 25px 0 90px 0;
  `;

  const ContentContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    @media screen and (max-width: 991px) {
      flex-direction: column;
      width: 99%;
    }
    @media screen and (min-width: 992px) {
      flex-direction: row;
      width: 99%;
    }
  `;

  const TextContainer = styled.div`
    height: 160px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-content: center;
    text-align: center;
  `;

  const RearTextContainer = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-content: center;
    text-align: center;
  `;

  const CardText = styled.div`
    color: ${({ theme }) => theme.palette.common.black};
    padding: 15px;
    @media screen and (max-width: 991px) {
      margin: 10px 0;
    }
    @media screen and (min-width: 992px) {
      margin: 10px 0 5px 0;
    }
  `;

  const BackCardText = styled(CardText)`
    height: 100%;
    margin: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overflow-wrap: anywhere;
    word-break: break-word;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
  `;

  const CardContent = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    transition: transform 0.7s ease;
    transform-style: preserve-3d;
    &.is-hoverable will-change: transform;
    transform-origin: center;
    transform: rotateY(0deg);
  `;

  const CardFaceFront = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  `;

  const CardFaceBack = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    transform: rotateY(180deg);
  `;

  const AnimatedKitCard = styled(KitCard)`
    transform-style: preserve-3d;
  `;

  const FlipWrapper = styled.div`
    perspective: 1000px;
    &.is-hoverable:hover .rotator {
      transform: rotateY(180deg);
    }
  `;

  return (
    <Container>
      <ContentContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            ml: 'auto',
            mr: 'auto',
            maxWidth: { sm: '540px', md: '1100px', lg: '1500px', xl: '1500px' },
          }}
        >
          <CardGrid>
            {fields.iconCardList &&
              fields.iconCardList.length > 0 &&
              fields.iconCardList
                .sort((a, b) => {
                  if (
                    parseInt(a.fields.sortOrder, 10) >
                    parseInt(b.fields.sortOrder, 10)
                  )
                    return 1;
                  if (
                    parseInt(a.fields.sortOrder, 10) <
                    parseInt(b.fields.sortOrder, 10)
                  )
                    return -1;
                  return 0;
                })
                .map((item, index) => (
                  <div key={index}>
                    {/** Determine if the card should flip on hover */}
                    {(() => {
                      const additionalInformation =
                        item?.fields?.additionalInformation || '';
                      const additionalInformationLink =
                        item?.fields?.additionalInformationLink;
                      const hasAdditionalInformationLink = Boolean(
                        additionalInformationLink?.href,
                      );
                      const shouldUseCompactBackText =
                        additionalInformation.length > 220;
                      const hoverable =
                        additionalInformation.length > 0 ||
                        hasAdditionalInformationLink;
                      return (
                        <FlipWrapper
                          className={hoverable ? 'is-hoverable' : ''}
                          style={{
                            width: '300px',
                            height: '320px',
                            margin: '0px 0 10px 0',
                            cursor: hoverable ? 'pointer' : 'default',
                          }}
                          tabIndex={hoverable ? 0 : -1}
                          role={hoverable ? 'button' : undefined}
                          aria-label={
                            hoverable
                              ? `More information: ${item?.fields?.cardText ?? ''}`
                              : (item?.fields?.cardText ?? undefined)
                          }
                          onFocus={(event) => {
                            if (!hoverable) return;
                            event.currentTarget.classList.add('is-focused');
                          }}
                          onBlur={(event) => {
                            if (!hoverable) return;
                            event.currentTarget.classList.remove('is-focused');
                            event.currentTarget.classList.remove('is-active');
                          }}
                          onKeyDown={(event) => {
                            if (!hoverable) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              event.currentTarget.classList.toggle('is-active');
                            }
                          }}
                        >
                          <CardContent className="rotator">
                            <CardFaceFront>
                              <AnimatedKitCard
                                style={{
                                  backgroundColor: buttonColor(
                                    theme,
                                    item?.fields?.frontCardColor === 'none'
                                      ? '#FFF'
                                      : item?.fields?.frontCardColor,
                                  ),
                                  width: '100%',
                                  height: '100%',
                                  padding: '8px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'flex-start',
                                  alignContent: 'center',
                                }}
                              >
                                <CardImg
                                  src={item?.fields?.cardIconImage?.url}
                                  alt={
                                    item?.fields?.cardIconImage?.label || 'Icon'
                                  }
                                />
                                <TextContainer>
                                  <CardText
                                    style={{
                                      fontSize:
                                        item?.fields?.frontFontSize === 'none'
                                          ? '1.10rem'
                                          : item?.fields?.frontFontSize,
                                      color:
                                        item?.fields?.frontTextColor === 'none'
                                          ? '#000'
                                          : item?.fields?.frontTextColor,
                                    }}
                                  >
                                    {item?.fields?.cardText}
                                  </CardText>
                                </TextContainer>
                              </AnimatedKitCard>
                            </CardFaceFront>
                            <CardFaceBack>
                              <AnimatedKitCard
                                style={{
                                  backgroundColor: buttonColor(
                                    theme,
                                    item?.fields?.backCardColor === 'none'
                                      ? '#FFF'
                                      : item?.fields?.backCardColor,
                                  ),
                                  width: '100%',
                                  height: '100%',
                                  padding: '8px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'flex-start',
                                  alignContent: 'center',
                                  overflow: 'hidden',
                                }}
                              >
                                <RearTextContainer>
                                  <BackCardText
                                    style={{
                                      fontSize:
                                        item?.fields?.backFontSize === 'none'
                                          ? shouldUseCompactBackText
                                            ? '0.95rem'
                                            : '1.10rem'
                                          : item?.fields?.backFontSize,
                                      color:
                                        item?.fields?.backTextColor === 'none'
                                          ? '#000'
                                          : item?.fields?.backTextColor,
                                      lineHeight:
                                        item?.fields?.backFontSize === 'none' &&
                                        shouldUseCompactBackText
                                          ? '1.35'
                                          : undefined,
                                    }}
                                  >
                                    {additionalInformation}
                                    {hasAdditionalInformationLink && (
                                      <DisplayLink
                                        link={additionalInformationLink}
                                        fallbackText="Read More"
                                        containerStyle={{ marginTop: '1rem' }}
                                      />
                                    )}
                                  </BackCardText>
                                </RearTextContainer>
                              </AnimatedKitCard>
                            </CardFaceBack>
                          </CardContent>
                        </FlipWrapper>
                      );
                    })()}
                  </div>
                ))}
          </CardGrid>
        </Box>
      </ContentContainer>
    </Container>
  );
};
export default IconCardList;
